import { Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { Router } from '@angular/router';
import { TranslocoService } from '@ngneat/transloco';
import { VcuService } from 'src/app/service/vcu.service';
import { PairGenerateResponse, VcuApiError, VcuDevice, sanitizeSerialNumber } from 'src/app/model/vcu-pair.model';
import { HttpErrorResponse } from '@angular/common/http';

export type PairDialogState = 'generating' | 'code-ready' | 'expired' | 'success' | 'error';

@Component({
  selector: 'app-vcu-pair-dialog',
  templateUrl: './vcu-pair-dialog.component.html',
  styleUrls: ['./vcu-pair-dialog.component.css']
})
export class VcuPairDialogComponent implements OnDestroy {
  @Input() visible: boolean = false;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() dialogClosed = new EventEmitter<void>();

  state: PairDialogState = 'generating';
  pairCode: string = '';
  codeDigits: string[] = [];
  errorMessage: string = '';

  // VCU detectada pelo polling
  pairedVcu: VcuDevice | null = null;

  // Timer countdown
  remainingSeconds: number = 0;
  timerDisplay: string = '5:00';
  private countdownInterval: any;

  // Polling para detectar pareamento
  private pollingInterval: any;
  // IDs das VCUs já pareadas antes de gerar o código — qualquer ID fora desse
  // conjunto no polling é a VCU recém-pareada. Mais preciso que comparar
  // contagem/data (imune a ordem e a respostas em cache).
  private knownVcuIds = new Set<string>();
  private readonly POLLING_INTERVAL_MS = 3000; // 3 segundos

  constructor(
    private vcuService: VcuService,
    private translocoService: TranslocoService,
    private router: Router
  ) {}

  ngOnDestroy(): void {
    this.clearTimer();
    this.stopPolling();
  }

  onDialogShow(): void {
    this.generateCode();
  }

  generateCode(): void {
    this.state = 'generating';
    this.clearTimer();
    this.stopPolling();
    this.pairedVcu = null;

    // Captura os IDs das VCUs ativas atuais antes de gerar o código
    this.vcuService.getPairedVcus().subscribe({
      next: (response) => {
        this.knownVcuIds = new Set(
          (response?.vcus ?? []).filter(v => v.status === 'AC').map(v => v.id)
        );
        this.requestPairCode();
      },
      error: () => {
        // Se falhar ao obter a baseline, usa conjunto vazio
        this.knownVcuIds = new Set();
        this.requestPairCode();
      }
    });
  }

  private requestPairCode(): void {
    this.vcuService.generatePairCode().subscribe({
      next: (response: PairGenerateResponse) => {
        this.pairCode = response.pairCode;
        this.codeDigits = response.pairCode.split('');
        this.remainingSeconds = response.expiresInSeconds;
        this.state = 'code-ready';
        this.startCountdown();
        this.startPolling();
      },
      error: (error: HttpErrorResponse) => {
        this.state = 'error';
        this.errorMessage = this.getErrorMessage(error);
      }
    });
  }

  // =============================================
  // Countdown Timer
  // =============================================

  private startCountdown(): void {
    this.updateTimerDisplay();

    this.countdownInterval = setInterval(() => {
      this.remainingSeconds--;

      if (this.remainingSeconds <= 0) {
        this.clearTimer();
        this.stopPolling();
        this.state = 'expired';
        return;
      }

      this.updateTimerDisplay();
    }, 1000);
  }

  private updateTimerDisplay(): void {
    const minutes = Math.floor(this.remainingSeconds / 60);
    const seconds = this.remainingSeconds % 60;
    this.timerDisplay = `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }

  private clearTimer(): void {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = null;
    }
  }

  get isTimerCritical(): boolean {
    return this.remainingSeconds > 0 && this.remainingSeconds < 60;
  }

  // =============================================
  // Polling para detectar pareamento automático
  // =============================================

  private startPolling(): void {
    this.pollingInterval = setInterval(() => {
      this.vcuService.getPairedVcus().subscribe({
        next: (response) => {
          const activeVcus = (response?.vcus ?? []).filter(v => v.status === 'AC');

          // Procura uma VCU cujo ID não estava na baseline → é a recém-pareada.
          const newVcu = activeVcus.find(v => !this.knownVcuIds.has(v.id));
          if (newVcu) {
            this.pairedVcu = newVcu;
            this.clearTimer();
            this.stopPolling();
            this.state = 'success';
          }
        },
        error: () => {
          // Silenciosamente ignora erros de polling — não interrompe o fluxo
        }
      });
    }, this.POLLING_INTERVAL_MS);
  }

  private stopPolling(): void {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
  }

  /** Serial da VCU pareada, ou texto amigável quando vier vazio/corrompido. */
  get displaySerial(): string {
    const serial = sanitizeSerialNumber(this.pairedVcu?.serialNumber);
    return serial ?? this.translocoService.translate('vcuPair.card.noSerial');
  }

  // =============================================
  // Tratamento de Erros
  // =============================================

  private getErrorMessage(error: HttpErrorResponse): string {
    // 401 — redirecionar para login
    if (error.status === 401) {
      this.closeDialog();
      this.router.navigate(['/login']);
      return this.translocoService.translate('vcuPair.errors.unauthorized');
    }

    // 403 — sem permissão
    if (error.status === 403) {
      return this.translocoService.translate('vcuPair.errors.forbidden');
    }

    // 500 — erro interno
    if (error.status === 500) {
      return this.translocoService.translate('vcuPair.errors.internalError');
    }

    const apiError = error.error as VcuApiError;

    if (apiError?.error) {
      switch (apiError.error) {
        case 'TOO_MANY_REQUESTS':
          return this.translocoService.translate('vcuPair.errors.tooManyRequests');
        case 'ALREADY_PAIRED':
          return this.translocoService.translate('vcuPair.errors.alreadyPaired');
        case 'NOT_FOUND':
          return this.translocoService.translate('vcuPair.errors.notFound');
        case 'FORBIDDEN':
          return this.translocoService.translate('vcuPair.errors.forbidden');
        case 'USER_NOT_FOUND':
          return this.translocoService.translate('vcuPair.errors.userNotFound');
        case 'INTERNAL_ERROR':
          return this.translocoService.translate('vcuPair.errors.internalError');
        default:
          return this.translocoService.translate('vcuPair.errors.generic');
      }
    }

    if (error.status === 0) {
      return this.translocoService.translate('vcuPair.errors.network');
    }

    return this.translocoService.translate('vcuPair.errors.generic');
  }

  // =============================================
  // Dialog Actions
  // =============================================

  closeDialog(): void {
    this.clearTimer();
    this.stopPolling();
    this.visible = false;
    this.visibleChange.emit(false);
    this.dialogClosed.emit();

    // Reset state para a próxima abertura
    setTimeout(() => {
      this.state = 'generating';
      this.pairCode = '';
      this.codeDigits = [];
      this.errorMessage = '';
      this.pairedVcu = null;
      this.remainingSeconds = 0;
      this.timerDisplay = '5:00';
      this.knownVcuIds = new Set();
    }, 300);
  }

  onDialogHide(): void {
    this.closeDialog();
  }
}
